import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Loader from "../../loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { domain } from "../../../security";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import {
  Tag,
  Link2,
  XCircle,
  Layers,
  Search,
  Scale,
  Plus,
  Trash2,
  Palette,
  AlignLeft,
  GripVertical,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";

// [COLOR_PRESETS and SearchableSelect components remain exactly the same as your original code]
const COLOR_PRESETS = [
  {
    label: "Blue",
    value: "blue",
    bg: "bg-blue-100",
    text: "text-blue-600",
    ring: "ring-blue-400",
  },
  {
    label: "Amber",
    value: "amber",
    bg: "bg-amber-100",
    text: "text-amber-600",
    ring: "ring-amber-400",
  },
  {
    label: "Slate",
    value: "slate",
    bg: "bg-slate-100",
    text: "text-slate-600",
    ring: "ring-slate-400",
  },
  {
    label: "Violet",
    value: "violet",
    bg: "bg-violet-100",
    text: "text-violet-600",
    ring: "ring-violet-400",
  },
  {
    label: "Emerald",
    value: "emerald",
    bg: "bg-emerald-100",
    text: "text-emerald-600",
    ring: "ring-emerald-400",
  },
  {
    label: "Rose",
    value: "rose",
    bg: "bg-rose-100",
    text: "text-rose-600",
    ring: "ring-rose-400",
  },
  {
    label: "Cyan",
    value: "cyan",
    bg: "bg-cyan-100",
    text: "text-cyan-600",
    ring: "ring-cyan-400",
  },
  {
    label: "Orange",
    value: "orange",
    bg: "bg-orange-100",
    text: "text-orange-600",
    ring: "ring-orange-400",
  },
  {
    label: "Yellow",
    value: "yellow",
    bg: "bg-yellow-100",
    text: "text-yellow-600",
    ring: "ring-yellow-400",
  },
];

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((o) => String(o.value) === String(value));

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setIsOpen(true);
    setSearch("");
  };

  useEffect(() => {
    const handle = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      )
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div
        ref={triggerRef}
        onClick={() => {
          if (!disabled) isOpen ? setIsOpen(false) : openDropdown();
        }}
        className={`w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white flex justify-between items-center transition h-[42px]
          ${disabled ? "opacity-60 cursor-not-allowed bg-slate-50" : "cursor-pointer hover:border-indigo-400"}`}
      >
        <span
          className={`truncate pr-2 ${selectedOption ? "text-slate-800 font-semibold" : "text-slate-400"}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white border border-slate-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-slate-50 p-2 border-b border-slate-100">
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Type to search…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length > 0 ? (
                filtered.map((o) => (
                  <div
                    key={o.value}
                    onClick={() => {
                      onChange(o.value);
                      setIsOpen(false);
                    }}
                    className={`px-3 py-2.5 text-sm cursor-pointer border-b border-slate-50 last:border-0 transition ${String(o.value) === String(value) ? "bg-indigo-50 text-indigo-700 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}
                  >
                    {o.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-center text-slate-400">
                  No results found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const LinkedProductsManager = ({ linkedProducts, onChange, products }) => {
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedUomId, setSelectedUomId] = useState("");
  const [uoms, setUoms] = useState([]);
  const [draggedLinkIdx, setDraggedLinkIdx] = useState(null);

  const handleProductChange = async (val) => {
    setSelectedProductId(val);
    setSelectedUomId("");
    setUoms([]);

    if (val) {
      try {
        const res = await axios.get(
          `${domain}/api/Products/${val}/pricing-uoms`,
        );
        setUoms(res.data);
      } catch (err) {
        toast.error("Failed to load UOMs for this product.");
      }
    }
  };

  const handleAdd = () => {
    if (!selectedProductId || !selectedUomId) {
      toast.warning("Please fill all linked product fields.");
      return;
    }

    const product = products.find((p) => p.id === Number(selectedProductId));
    const uom = uoms.find((u) => u.id === Number(selectedUomId));

    if (linkedProducts.some((c) => c.productId === product.id)) {
      toast.warning("This product is already linked.");
      return;
    }

    onChange([
      ...linkedProducts,
      {
        productId: product.id,
        productName: product.productName,
        itemCode: product.itemCode || product.ItemCode,
        uomId: uom.id,
        uomName: uom.name,
        priority: linkedProducts.length + 1, // Default to lowest priority
      },
    ]);

    setSelectedProductId("");
    setSelectedUomId("");
    setUoms([]);
  };

  const handleRemove = (id) => {
    const updated = linkedProducts.filter((lp) => lp.productId !== id);
    // Re-adjust priorities
    onChange(updated.map((item, index) => ({ ...item, priority: index + 1 })));
  };

  // Drag and Drop reordering logic for priorities
  const handleDragStart = (e, index) => {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", e.target.parentNode);
    }
    setDraggedLinkIdx(index);
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedLinkIdx === null || draggedLinkIdx === index) return;

    const newLinks = [...linkedProducts];
    const draggedItem = newLinks[draggedLinkIdx];
    newLinks.splice(draggedLinkIdx, 1);
    newLinks.splice(index, 0, draggedItem);

    // Auto re-assign priorities based on new order
    onChange(newLinks.map((item, i) => ({ ...item, priority: i + 1 })));
    setDraggedLinkIdx(index);
  };

  const handleDragEnd = () => setDraggedLinkIdx(null);

  const productOptions = products.map((p) => ({
    value: p.id,
    label: `${p.productName} ${p.itemCode ? `(${p.itemCode})` : ""}`,
  }));

  return (
    <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4 ml-0 lg:ml-[30px] shadow-inner mt-2">
      {/* Priority Waterfall Explanation Alert */}
      <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-lg p-3 flex gap-2 items-start shadow-sm">
        <AlertCircle size={16} className="shrink-0 mt-0.5 text-blue-500" />
        <div>
          <strong className="block mb-1 text-blue-900">
            Priority-Based Auto Deduction (Waterfall)
          </strong>
          The purpose of prioritization is to let the system automatically know
          which item to use first. Drag the grip icon (
          <GripVertical size={12} className="inline opacity-50" />) to reorder
          items.
          <br />
          If a batch requires 100kg and Priority 1 has 60kg in stock, the system
          will automatically deduct 60kg from Priority 1, and the remaining 40kg
          from Priority 2.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mb-4">
        <div className="md:col-span-7">
          <label className="block text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-1">
            Product <span className="text-red-400">*</span>
          </label>
          <SearchableSelect
            options={productOptions}
            value={selectedProductId}
            onChange={handleProductChange}
            placeholder="Search Regular Products..."
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-1">
            Unit (UOM) <span className="text-red-400">*</span>
          </label>
          <select
            value={selectedUomId}
            onChange={(e) => setSelectedUomId(e.target.value)}
            disabled={!selectedProductId}
            className="w-full border border-slate-200 rounded-xl px-3 h-[42px] text-sm bg-white disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">Select UOM...</option>
            {uoms.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} {u.type ? `(${u.type})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <button
            type="button"
            onClick={handleAdd}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-[42px] rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition shadow-sm"
          >
            <Plus size={16} /> Link
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-4 py-2.5 w-[50px] text-center"></th>
              <th className="px-4 py-2.5 text-xs font-bold text-slate-400 uppercase">
                Priority
              </th>
              <th className="px-4 py-2.5 text-xs font-bold text-slate-400 uppercase">
                Product
              </th>
              <th className="px-4 py-2.5 text-xs font-bold text-slate-400 uppercase text-center">
                Unit
              </th>
              <th className="px-4 py-2.5 text-xs font-bold text-slate-400 uppercase text-right">
                Remove
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {linkedProducts.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-slate-400 text-xs italic"
                >
                  No products linked yet.
                </td>
              </tr>
            ) : (
              linkedProducts.map((c, index) => (
                <tr
                  key={c.productId}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  className={`hover:bg-slate-50 transition ${draggedLinkIdx === index ? "opacity-50 bg-slate-100" : ""}`}
                >
                  <td className="px-4 py-2.5 text-center cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600">
                    <GripVertical size={16} className="mx-auto" />
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black px-2 py-1 rounded">
                      PRIORITY {index + 1}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <p className="font-semibold text-slate-700 text-xs">
                      {c.productName}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono">
                      {c.itemCode}
                    </p>
                  </td>
                  <td className="px-4 py-2.5 text-center text-slate-600 text-xs font-medium">
                    {c.uomName}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => handleRemove(c.productId)}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AddProductionTagging = ({ onClose, refreshData, taggingToEdit }) => {
  const currentUserID = useSelector(selectUserID);

  const [groupName, setGroupName] = useState("");
  const [tags, setTags] = useState([]);
  const [products, setProducts] = useState([]);
  const [uomList, setUomList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDependencies, setLoadingDependencies] = useState(true);
  const [expandedTagId, setExpandedTagId] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingDependencies(true);
      try {
        const [prodRes, uomRes] = await Promise.all([
          axios.get(
            `${domain}/api/Products/by-item-type-category?category=Regular`,
          ),
          axios.get(`${domain}/api/UnitOfMeasurements`),
        ]);

        setProducts(prodRes.data || []);
        const fetchedUoms = uomRes.data || [];
        setUomList(fetchedUoms);

        const defaultUnitCode =
          fetchedUoms.length > 0 ? fetchedUoms[0].code : "kg";

        if (taggingToEdit) {
          setGroupName(taggingToEdit.groupName || "");
          setTags([
            {
              id: taggingToEdit.id ?? taggingToEdit.Id,
              tagName: taggingToEdit.tagName || "",
              unit: taggingToEdit.unit || defaultUnitCode,
              color: taggingToEdit.color || "blue",
              sortOrder: taggingToEdit.sortOrder ?? 1,
              // Pre-fills linkedProducts accurately, keeping backend priority sorting intact
              linkedProducts: taggingToEdit.linkedProducts || [],
            },
          ]);
        } else {
          setGroupName("");
          setTags([
            {
              id: Date.now(),
              tagName: "",
              unit: defaultUnitCode,
              color: "blue",
              sortOrder: 1,
              linkedProducts: [],
            },
          ]);
        }
      } catch (err) {
        toast.error("Failed to load dependency data.");
      } finally {
        setLoadingDependencies(false);
      }
    };
    fetchData();
  }, [taggingToEdit]);

  const updateTag = (id, field, value) => {
    setTags((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)),
    );
  };

  const updateLinkedProducts = (id, updatedLinks) => {
    setTags((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, linkedProducts: updatedLinks } : t,
      ),
    );
  };

  const addTagRow = () => {
    const defaultUnitCode = uomList.length > 0 ? uomList[0].code : "kg";
    setTags((prev) => [
      ...prev,
      {
        id: Date.now(),
        tagName: "",
        unit: defaultUnitCode,
        color: "blue",
        sortOrder: prev.length + 1,
        linkedProducts: [],
      },
    ]);
  };

  const removeTagRow = (id) => {
    setTags((prev) => {
      const filtered = prev.filter((t) => t.id !== id);
      return filtered.map((t, index) => ({ ...t, sortOrder: index + 1 }));
    });
  };

  const toggleExpand = (id) =>
    setExpandedTagId((prev) => (prev === id ? null : id));

  const handleDragStart = (e, index) => {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", e.target.parentNode);
    }
    setDraggedIdx(index);
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === index) return;
    setTags((prev) => {
      const newTags = [...prev];
      const draggedItem = newTags[draggedIdx];
      newTags.splice(draggedIdx, 1);
      newTags.splice(index, 0, draggedItem);
      return newTags.map((t, i) => ({ ...t, sortOrder: i + 1 }));
    });
    setDraggedIdx(index);
  };

  const handleDragEnd = () => setDraggedIdx(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) return toast.error("Section Header is required.");
    if (tags.find((t) => !t.tagName.trim()))
      return toast.error("All tag items must have a Line/Tag Name.");

    setIsLoading(true);

    try {
      if (taggingToEdit) {
        const payload = {
          TagName: tags[0].tagName.trim(),
          GroupName: groupName.trim(),
          Unit: tags[0].unit,
          Color: tags[0].color,
          SortOrder: parseInt(tags[0].sortOrder, 10) || 1,
          UserID: currentUserID,
          LinkedProducts: tags[0].linkedProducts.map((lp, i) => ({
            ProductId: lp.productId,
            UomId: lp.uomId,
            Priority: i + 1, // Guarantee priority is mapped in sequence
          })),
        };
        await axios.put(
          `${domain}/api/ProductionTaggings/${taggingToEdit.id ?? taggingToEdit.Id}`,
          payload,
        );
        toast.success("Production tag updated.");
      } else {
        const promises = tags.map((t) => {
          const payload = {
            TagName: t.tagName.trim(),
            GroupName: groupName.trim(),
            Unit: t.unit,
            Color: t.color,
            SortOrder: parseInt(t.sortOrder, 10) || 1,
            UserID: currentUserID,
            LinkedProducts: t.linkedProducts.map((lp, i) => ({
              ProductId: lp.productId,
              UomId: lp.uomId,
              Priority: i + 1,
            })),
          };
          return axios.post(`${domain}/api/ProductionTaggings`, payload);
        });

        await Promise.all(promises);
        toast.success(`${tags.length} Production tag(s) created successfully.`);
      }
      refreshData();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message?.title ||
          err.response?.data?.message ||
          "Something went wrong.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 lg:p-8">
      <div className="flex h-full max-h-[95vh] w-full max-w-[1200px] flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-200 relative">
        {(isLoading || loadingDependencies) && <Loader />}

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 rounded-xl">
              <Layers size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 leading-tight">
                {taggingToEdit
                  ? "Edit Production Tag"
                  : "Add Production Group & Tags"}
              </h2>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                Configuration for Batch Recording Form
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto p-4 lg:p-6 bg-slate-50/50">
          <form id="tag-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-500 mb-3">
                <Layers size={14} /> 1. Section Header / Category
              </label>
              <div className="relative max-w-lg">
                <input
                  type="text"
                  required
                  placeholder="e.g. Mix Materials, Aggregates, Admixtures"
                  className="w-full pl-4 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none font-bold text-slate-800 transition shadow-sm"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-4">
                <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-600">
                  <AlignLeft size={14} /> 2. Tag Line Items
                </label>
                {!taggingToEdit && (
                  <button
                    type="button"
                    onClick={addTagRow}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl text-xs font-bold transition-colors"
                  >
                    <Plus size={14} /> Add Another Tag Item
                  </button>
                )}
              </div>

              <div className="hidden lg:grid grid-cols-[30px_2.5fr_130px_2.5fr_130px_40px] gap-4 px-2 mb-2 text-[10px] font-black text-slate-400 uppercase tracking-widest items-center">
                <div></div>
                <div>
                  Line Name / Tag <span className="text-red-400">*</span>
                </div>
                <div>Unit</div>
                <div>
                  Linked Products{" "}
                  <span className="text-slate-300 font-normal">(Optional)</span>
                </div>
                <div>Display Color</div>
                <div></div>
              </div>

              <div className="space-y-4 lg:space-y-3">
                {tags.map((tag, index) => {
                  const activeColor =
                    COLOR_PRESETS.find((c) => c.value === tag.color) ||
                    COLOR_PRESETS[0];
                  const isDragging = draggedIdx === index;
                  const isExpanded = expandedTagId === tag.id;

                  return (
                    <div key={tag.id} className="flex flex-col gap-2">
                      <div
                        draggable={!taggingToEdit}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        className={`grid grid-cols-1 lg:grid-cols-[30px_2.5fr_130px_2.5fr_130px_40px] gap-4 items-center bg-slate-50 lg:bg-transparent p-4 lg:p-0 rounded-xl lg:rounded-none border lg:border-none border-slate-200 group transition-all duration-200 ${isDragging ? "opacity-50 scale-[0.99] bg-slate-100/80 rounded-xl border-dashed" : ""}`}
                      >
                        <div
                          className={`hidden lg:flex justify-center text-slate-300 hover:text-slate-600 transition-colors ${taggingToEdit ? "opacity-30 cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}`}
                          title="Drag to reorder"
                        >
                          <GripVertical size={16} />
                        </div>

                        <div>
                          <label className="block lg:hidden text-[10px] font-bold text-slate-400 uppercase mb-1">
                            Tag Name
                          </label>
                          <div className="relative">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                              type="text"
                              required
                              placeholder="e.g. 3/4 Gravel"
                              className="w-full pl-9 pr-3 py-2.5 h-[42px] border border-slate-200 bg-white rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none text-sm font-semibold transition"
                              value={tag.tagName}
                              onChange={(e) =>
                                updateTag(tag.id, "tagName", e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block lg:hidden text-[10px] font-bold text-slate-400 uppercase mb-1">
                            Unit
                          </label>
                          <select
                            value={tag.unit}
                            onChange={(e) =>
                              updateTag(tag.id, "unit", e.target.value)
                            }
                            className="w-full py-2.5 px-3 h-[42px] border border-slate-200 bg-white rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-100 outline-none"
                          >
                            {uomList.length > 0 ? (
                              uomList.map((uom) => (
                                <option key={uom.id} value={uom.code}>
                                  {uom.code} - {uom.unitName}
                                </option>
                              ))
                            ) : (
                              <>
                                <option value="kg">kg - Kilogram</option>
                                <option value="L">L - Liter</option>
                              </>
                            )}
                          </select>
                        </div>

                        <div>
                          <label className="block lg:hidden text-[10px] font-bold text-slate-400 uppercase mb-1">
                            Linked Products
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleExpand(tag.id)}
                            className={`w-full py-2.5 px-3 h-[42px] border rounded-xl text-sm font-semibold flex justify-between items-center transition ${isExpanded ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}
                          >
                            <span className="flex items-center gap-2 truncate">
                              <Link2
                                size={16}
                                className={
                                  isExpanded
                                    ? "text-indigo-500"
                                    : "text-slate-400"
                                }
                              />
                              {tag.linkedProducts.length > 0
                                ? `${tag.linkedProducts.length} Product${tag.linkedProducts.length > 1 ? "s" : ""}`
                                : "Add Products"}
                            </span>
                            {isExpanded ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </button>
                        </div>

                        <div>
                          <label className="block lg:hidden text-[10px] font-bold text-slate-400 uppercase mb-1">
                            Color
                          </label>
                          <div className="relative">
                            <select
                              value={tag.color}
                              onChange={(e) =>
                                updateTag(tag.id, "color", e.target.value)
                              }
                              className={`w-full py-2.5 pl-8 pr-3 h-[42px] border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-100 outline-none appearance-none transition-colors ${activeColor.bg} ${activeColor.text}`}
                            >
                              {COLOR_PRESETS.map((preset) => (
                                <option key={preset.value} value={preset.value}>
                                  {preset.label}
                                </option>
                              ))}
                            </select>
                            <Palette
                              size={14}
                              className={`absolute left-3 top-1/2 -translate-y-1/2 ${activeColor.text}`}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-end lg:justify-center">
                          {tags.length > 1 && !taggingToEdit && (
                            <button
                              type="button"
                              onClick={() => removeTagRow(tag.id)}
                              className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                              title="Remove Line"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <LinkedProductsManager
                          linkedProducts={tag.linkedProducts}
                          onChange={(links) =>
                            updateLinkedProducts(tag.id, links)
                          }
                          products={products}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PREVIEW BLOCK (Kept exact same) */}
            <div className="p-5 bg-slate-100 rounded-2xl border border-slate-200">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">
                Preview Representation
              </p>
              <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm max-w-2xl">
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-4 border-b border-slate-100 pb-2">
                  {groupName || "[ Section Header ]"}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {tags.map((tag) => {
                    const preset =
                      COLOR_PRESETS.find((c) => c.value === tag.color) ||
                      COLOR_PRESETS[0];
                    return (
                      <div
                        key={tag.id}
                        className={`flex flex-col gap-1 p-2.5 rounded-xl border border-${preset.value}-200 ${preset.bg} w-[160px]`}
                      >
                        <span
                          className={`text-xs font-bold ${preset.text} flex items-center gap-1.5`}
                        >
                          <Tag size={12} /> {tag.tagName || "Item Name"}
                        </span>
                        <div className="relative">
                          <input
                            disabled
                            placeholder="0.00"
                            className="w-full h-8 px-2 text-right rounded bg-white/60 border border-transparent text-sm font-semibold outline-none"
                          />
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 uppercase">
                            {tag.unit}
                          </span>
                        </div>
                        {tag.linkedProducts.length > 0 && (
                          <div className="mt-1 space-y-1 border-t border-black/5 pt-1">
                            {tag.linkedProducts.map((lp, i) => (
                              <div
                                key={lp.productId}
                                className="text-[9px] font-bold flex items-center gap-1 text-slate-500 leading-tight"
                              >
                                <span className="bg-black/10 rounded px-1 min-w-[12px] text-center">
                                  {i + 1}
                                </span>
                                <span
                                  className="truncate"
                                  title={`${lp.uomName} - ${lp.productName}`}
                                >
                                  [{lp.uomName}] {lp.productName}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-white shrink-0 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            form="tag-form"
            type="submit"
            disabled={isLoading || loadingDependencies}
            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-black text-sm rounded-xl shadow-md shadow-indigo-200 transition-all active:scale-95 flex items-center gap-2"
          >
            {taggingToEdit ? "Update Configuration" : "Save Group & Tags"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProductionTagging;
